// components/feed/HomeFeedSection.jsx
// Fetches all feeds in parallel, merges + sorts by most recent, renders a flat FeedItem list.
// Stale-while-revalidate: shows cached data immediately, fetches fresh, deletes old cache and stores new.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'react-bootstrap-icons';
import { Spinner } from 'react-bootstrap';
import FeedItem from './FeedItem';
import './FeedItem.css';

const API_KEY = process.env.REACT_APP_API_KEY;
const AUTH_HEADERS = {
  'Content-Type': 'application/json',
  // eslint-disable-next-line no-useless-concat
  'Authorization': 'Bearer ' + API_KEY,
};

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

function cacheGet(key) {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    return JSON.parse(data);
  } catch { return null; }
}

function cacheSet(key, value) {
  try {
    // Delete old entry first, then write fresh
    localStorage.removeItem(key);
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* storage full — skip */ }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseFeedEndpoint(apiEndpoint) {
  if (!apiEndpoint) return null;
  const clean = apiEndpoint.replace(/^[&?]+/, '').replace(/\?/g, '&');
  const p = new URLSearchParams(clean);
  const schema = p.get('schema') || '';
  const table  = p.get('table') || p.get('table_name') || '';
  if (!schema || !table) return null;
  return {
    schema, table,
    filterColumn: p.get('filter_column') || '',
    filterString: p.get('filter_string') || '',
    isDepartment: clean.includes('table_name='),
    clean,
  };
}

function buildApiUrl(feed, limit = 10) {
  const parsed = parseFeedEndpoint(feed.apiEndpoint);
  if (!parsed) return null;
  const { schema, table, filterColumn, filterString, isDepartment, clean } = parsed;
  if (isDepartment) {
    return `https://api.govglance.org/departments/?schema_name=united_states_of_america&${clean}&limit=${limit}`;
  }
  const params = new URLSearchParams({ schema, table, limit: String(limit), order_by: 'created_at', skip: '0' });
  if (filterColumn && filterString) { params.set('filter_column', filterColumn); params.set('filter_string', filterString); }
  return `https://api.govglance.org/posts/recent?${params.toString()}`;
}

function buildFeedPageUrl(feed) {
  const parsed = parseFeedEndpoint(feed.apiEndpoint);
  if (!parsed) return null;
  const { schema, table, filterColumn, filterString } = parsed;
  const qs = new URLSearchParams();
  if (filterColumn && filterString) { qs.set('filterColumn', filterColumn); qs.set('filterString', filterString); }
  const q = qs.toString();
  return q ? `/feed/${schema}/${table}?${q}` : `/feed/${schema}/${table}`;
}

function getDateValue(item) {
  const raw = item.created_at || item.date_issued || item.action_date || item.start_date || '';
  return raw ? new Date(raw).getTime() : 0;
}

// ---------------------------------------------------------------------------
// FeedLabel
// ---------------------------------------------------------------------------

const FeedLabel = ({ name, url }) => {
  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    if (url) { window.scrollTo(0, 0); navigate(url); }
  }, [url, navigate]);
  return (
    <button className="hfs-feed-label" onClick={handleClick} type="button">
      <span className="hfs-feed-label-name">{name}</span>
      <ChevronRight className="hfs-feed-label-chevron" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// HomeFeedSection
// ---------------------------------------------------------------------------

/**
 * Props:
 *  feeds        – array of feed objects from feedsConfig
 *  itemsPerFeed – how many items to fetch per feed (default 10)
 *  className    – optional wrapper class
 *  searchQuery  – optional string to filter displayed items
 */
const HomeFeedSection = ({ feeds = [], itemsPerFeed = 10, className = '', searchQuery = '' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const validFeeds = useMemo(
    () => feeds.filter((f) => f.apiEndpoint && (!f.type || f.type === 'normal')),
    [feeds],
  );

  // Track the previous cache key so we can delete it when feeds change
  const prevCacheKeyRef = React.useRef(null);

  useEffect(() => {
    if (validFeeds.length === 0) { setLoading(false); return; }
    let cancelled = false;

    const cacheKey = 'hfs_' + validFeeds.map(f => f.id || f.feedName).join('|') + '_' + itemsPerFeed;

    // If feeds changed, delete the old cache entry — it no longer matches current topics
    if (prevCacheKeyRef.current && prevCacheKeyRef.current !== cacheKey) {
      try { localStorage.removeItem(prevCacheKeyRef.current); } catch {}
    }
    prevCacheKeyRef.current = cacheKey;

    // 1. Show cached data immediately (stale)
    const cached = cacheGet(cacheKey);
    if (cached) {
      setItems(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // 2. Fetch fresh from all feeds in parallel
    const requests = validFeeds.map((feed) => {
      const url    = buildApiUrl(feed, itemsPerFeed);
      const parsed = parseFeedEndpoint(feed.apiEndpoint);
      if (!url || !parsed) return Promise.resolve([]);

      return fetch(url, { method: 'GET', headers: AUTH_HEADERS })
        .then((r) => (r.ok ? r.json() : []))
        .then((json) => {
          const results = Array.isArray(json) ? json : (json.data || json.results || []);
          return results.map((item) => ({
            ...item,
            _feed: {
              feedName:    feed.feedName || feed.title || '',
              branch:      feed.branch   || feed.feedBranch || '',
              topic:       feed.topic    || feed.feedTopic  || '',
              buttonName:  feed.buttonName || 'Document',
              schema:      parsed.schema,
              table:       parsed.table,
              feedPageUrl: buildFeedPageUrl(feed),
            },
          }));
        })
        .catch(() => []);
    });

    Promise.all(requests).then((groups) => {
      if (cancelled) return;
      const merged = groups.flat().sort((a, b) => getDateValue(b) - getDateValue(a));
      // Delete old entry and store fresh data under current key
      cacheSet(cacheKey, merged);
      setItems(merged);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [validFeeds, itemsPerFeed]);

  // Filter by search query
  const displayItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      item.title?.toLowerCase().includes(q) ||
      item.document_title?.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  // One item per unique feed (schema|table), most recent wins since sorted descending
  const listItems = useMemo(() => {
    const seen = new Set();
    return displayItems.filter((item) => {
      const key = `${item._feed.schema}|${item._feed.table}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [displayItems]);

  if (loading && items.length === 0) {
    return (
      <div className="hfs-loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (listItems.length === 0) return null;

  return (
    <div className={`home-feed-section ${className}`}>
      {listItems.map((item, idx) => {
        const prevFeed = idx > 0 ? listItems[idx - 1]._feed.feedName : null;
        const showLabel = item._feed.feedName !== prevFeed;
        return (
          <React.Fragment key={item.id || item.url || idx}>
            {showLabel && <FeedLabel name={item._feed.feedName} url={item._feed.feedPageUrl} />}
            <FeedItem
              item={item}
              feedName={item._feed.feedName}
              branch={item._feed.branch}
              topic={item._feed.topic}
              buttonName={item._feed.buttonName}
              schema={item._feed.schema}
              table={item._feed.table}
              expanded={false}
              isRecentsItem={false}
              variant="list"
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default HomeFeedSection;

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------
const styles = `
.home-feed-section {
  width: 100%;
  padding: 20px 0 24px 0;
}

.hfs-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
  color: var(--text-muted);
}

.hfs-feed-label {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px 6px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  margin-top: 8px;
}

.hfs-feed-label:first-child {
  margin-top: 0;
}

.hfs-feed-label-name {
  font-size: 17px;
  font-weight: bold;
  color: var(--text-primary);
}

.hfs-feed-label-chevron {
  width: 18px;
  height: 18px;
  color: var(--text-primary);
  flex-shrink: 0;
}

.hfs-feed-label:hover .hfs-feed-label-name,
.hfs-feed-label:hover .hfs-feed-label-chevron {
  opacity: 0.75;
}
`;

if (typeof document !== 'undefined' && !document.getElementById('hfs-styles')) {
  const el = document.createElement('style');
  el.id = 'hfs-styles';
  el.textContent = styles;
  document.head.appendChild(el);
}